import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import TransactionModal from "./TransactionModal";
import { CheckCircleOutline, Payment } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import CustomButton from "../../Components/CustomButton";
import PageTitle from "../../Components/PageTitle";

const QRPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const data = location.state?.feeData?.data;
  const [openModal, setOpenModal] = useState(false);

  const [copied, setCopied] = useState(false);
  const upiId = data.upiId || "msola33052@ybl";

  const handleCopy = () => {
    navigator.clipboard.writeText(upiId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    });
  };

  if (!data) {
    return (
      <>
        <Navbar />
        <PageTitle
          title="Scan & Pay - Shree Mangalam School"
          description="Quickly scan the QR code to make secure online payments to Shree Mangalam School. Fast, simple, and safe digital fee payment method."
          url="/qr-page"
          keywords="QR code payment, scan and pay, online school fees, UPI payment, Shree Manglam fee QR, RIMC school QR code"
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: "center",
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontOpticalSizing: "auto",
                fontStyle: "normal",
                fontSize: "25px",
                letterSpacing: 1,
                color: "#065A79",
                fontWeight: "bold",
              }}
            >
              Payment Data Not Found
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                fontFamily: "Montserrat, sans-serif",
                fontOpticalSizing: "auto",
                fontStyle: "normal",
                letterSpacing: 1,
                lineHeight: "1.8rem",
              }}
            >
              We couldn't retrieve your payment details. Please initiate the
              payment again.
            </Typography>

            <Link to="/online-fees">
              <CustomButton ButtonName={"Go Back to Payment"} />
            </Link>
          </Paper>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
          p: 3,
        }}
      >
        <Card
          sx={{
            maxWidth: 450,
            width: "100%",
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: "#065A79",
              color: theme.palette.primary.contrastText,
              p: 3,
              textAlign: "center",
            }}
          >
            <Payment sx={{ fontSize: 50 }} />
            <Typography
              variant="h5"
              sx={{
                mt: 1,
                fontFamily: "Montserrat, sans-serif",
                fontOpticalSizing: "auto",
                fontStyle: "normal",
                letterSpacing: 1,
                lineHeight: "1.8rem",
              }}
            >
              {/* Pay School Fees */}
              Scan & Pay
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontOpticalSizing: "auto",
                fontStyle: "normal",
                letterSpacing: 1,
                lineHeight: "1.8rem",
              }}
            >
              {/* Scan the QR code below */}
              Please scan the QR code below to pay your fees.
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "Montserrat, sans-serif",
                  fontOpticalSizing: "auto",
                  fontStyle: "normal",
                  letterSpacing: 1,
                  lineHeight: "1.8rem",
                }}
              >
                Total Amount: ₹{data.totalFees}
              </Typography>

              <Box
                sx={{
                  p: 2,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  position: "relative",
                  mb: 2,
                }}
              >
                <img
                  src={data.qrCodeUrl}
                  alt="UPI QR Code"
                  style={{
                    width: 220,
                    height: 220,
                    objectFit: "contain",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -10,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: "background.paper",
                    px: 1,
                    borderRadius: 1,
                    boxShadow: theme.shadows[1],
                  }}
                >
                  <Typography variant="caption" color="#AD1C86">
                    Scan to Pay
                  </Typography>
                </Box>
              </Box>

              {/* <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontFamily: "Montserrat, sans-serif",
                  fontOpticalSizing: "auto",
                  fontStyle: "normal",
                  letterSpacing: 1,
                  lineHeight: "1.8rem",
                }}
              >
                UPI ID: <strong>{data.upiId || "msola33052@ybl"}</strong>
              </Typography> */}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontFamily: "Montserrat, sans-serif",
                  fontOpticalSizing: "auto",
                  fontStyle: "normal",
                  letterSpacing: 1,
                  lineHeight: "1.8rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                UPI ID: <strong>{upiId}</strong>
                <Tooltip title={copied ? "Copied!" : "Copy UPI ID"}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>

              <Divider sx={{ width: "100%", my: 1 }} />

              <Box onClick={() => setOpenModal(true)}>
                <CustomButton ButtonName={"I've Made the Payment"} />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 2,
                  fontFamily: "Montserrat, sans-serif",
                  fontOpticalSizing: "auto",
                  fontStyle: "normal",
                  letterSpacing: 1,
                }}
              >
                After payment, please enter your transaction ID for verification
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <TransactionModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          feeId={data._id}
        />
      </Box>
      <Footer />
    </>
  );
};

export default QRPage;
